import { useCallback, useRef } from 'react';
import useMount from 'hooks/use-mount';

import { ENV, RECAPTCHA_URL } from 'utils/constants';

const createRecaptchaStyle = () => {
  const style = document.createElement('style');
  style.innerHTML = `
    .grecaptcha-badge { 
      visibility: hidden !important;
      opacity: 0 !important;
    }
  `;
  style.id = 'hide-recaptcha-badge';
  document.head.appendChild(style);
};

const removeRecaptchaStyle = () => {
  const style = document.getElementById('hide-recaptcha-badge');
  if (style) {
    document.head.removeChild(style);
  }
};

const useRecaptcha = action => {
  const recaptchaRef = useRef(null);

  const siteKey = ENV.recaptchaSiteKey;

  useMount(() => {
    removeRecaptchaStyle();

    if (!window.grecaptcha) {
      const script = document.createElement('script');
      script.src = `${RECAPTCHA_URL}${siteKey}`;
      script.async = true;
      script.defer = true;
      script.id = 'recaptcha-script';

      script.onload = () => {
        window.grecaptcha.ready(() => {
          recaptchaRef.current = window.grecaptcha;
        });
      };

      document.head.appendChild(script);

      return () => {
        const script = document.getElementById('recaptcha-script');
        if (script) {
          document.head.removeChild(script);
        }

        createRecaptchaStyle();
      };
    } else {
      window.grecaptcha.ready(() => {
        recaptchaRef.current = window.grecaptcha;
      });

      return () => {
        createRecaptchaStyle();
      };
    }
  });

  const executeRecaptcha = useCallback(async () => {
    const currentRecaptcha = recaptchaRef.current || window.grecaptcha;

    if (!currentRecaptcha) {
      console.warn('reCAPTCHA not loaded yet');
      return null;
    }

    try {
      const token = await currentRecaptcha.execute(siteKey, { action });
      return token;
    } catch (error) {
      console.error('reCAPTCHA execution failed:', error);
      return null;
    }
  }, [action, siteKey]);

  return {
    executeRecaptcha,
  };
};

export default useRecaptcha;
